/**
 * Level trajectory sparkline — the candidate's estimated level across rounds,
 * drawn as a compact step line. Restores the sparkline deleted in the
 * panel-branch merge (`docs/labs/level-assessment/multi-round-narrative-layer.md`
 * §4 — the reference was `trajectory.tsx` at `cb42ec55`), rebuilt smaller and
 * simplified to the outcomes the timeline itself renders:
 *
 *  - `scored`                        → solid red dot, solid segment
 *  - `failed`                        → amber hollow dot (attention, not alarm)
 *  - everything else (carried, no_new_signal, evaluating, pending, abstained)
 *    → hollow ink dot, dashed/faded segment — the level didn't move THIS round
 *
 * y is the overall MEAN (not the rounded level), carried forward across every
 * round that didn't itself produce a fresh score — so the line reads as a
 * continuous read of "what we believed at round N", not a series of unrelated
 * points. Purely derived from the timeline read-model (`CandidateTimelineRound[]`).
 *
 * The SVG also renders a small "Evolução" title and an `R{n}` label centered
 * below each round dot so the trajectory is readable without relying on the
 * separate dispersion chip.
 */

import type { CandidateTimelineRound } from "@shared/labs/level-assessment-candidate";
import { C, MONO, UI } from "../results/ui-tokens";

type PointKind = "scored" | "failed" | "hollow";

interface SparkPoint {
  id: string;
  y: number | null;
  kind: PointKind;
}

function buildPoints(rounds: CandidateTimelineRound[]): SparkPoint[] {
  let carried: number | null = null;
  return rounds.map((r) => {
    const freshlyScored = r.outcome === "scored" && r.currentEvaluation?.overallMean != null;
    if (freshlyScored) carried = r.currentEvaluation?.overallMean ?? carried;
    const kind: PointKind = freshlyScored ? "scored" : r.outcome === "failed" ? "failed" : "hollow";
    return { id: r.id, y: carried, kind };
  });
}

export function TrajectorySparkline({
  rounds,
  levels,
}: {
  rounds: CandidateTimelineRound[];
  levels: { min: number; max: number };
}) {
  const W = 130;
  const H = 52;
  const padX = 6;
  const titleTop = 9;
  const sparkTop = 14;
  const sparkBottom = 36;
  const labelBottom = 46;
  const pts = buildPoints(rounds);
  const n = pts.length;
  const span = Math.max(1, levels.max - levels.min);

  const x = (i: number) => (n <= 1 ? W / 2 : padX + (i / (n - 1)) * (W - 2 * padX));
  const y = (v: number) => sparkBottom - ((v - levels.min) / span) * (sparkBottom - sparkTop);

  const segments = pts.map((p, i) => {
    if (i === 0 || p.y === null) return null;
    const prev = pts[i - 1];
    if (prev.y === null) return null;
    const x0 = x(i - 1);
    const y0 = y(prev.y);
    const x1 = x(i);
    const y1 = y(p.y);
    const soft = p.kind !== "scored";
    return (
      <path
        key={`seg-${p.id}`}
        d={`M${x0} ${y0} H${(x0 + x1) / 2} V${y1} H${x1}`}
        fill="none"
        stroke={p.kind === "failed" ? C.amberFg : C.red}
        strokeWidth={1.5}
        opacity={soft ? 0.45 : 1}
        strokeDasharray={soft ? "3 3" : undefined}
      />
    );
  });

  const dots = pts.map((p, i) => {
    if (p.y === null) return null;
    if (p.kind === "scored") {
      return <circle key={`dot-${p.id}`} cx={x(i)} cy={y(p.y)} r={3} fill={C.red} />;
    }
    if (p.kind === "failed") {
      return (
        <circle
          key={`dot-${p.id}`}
          cx={x(i)}
          cy={y(p.y)}
          r={3}
          fill={C.white}
          stroke={C.amberFg}
          strokeWidth={1.6}
        />
      );
    }
    return (
      <circle
        key={`dot-${p.id}`}
        cx={x(i)}
        cy={y(p.y)}
        r={2.6}
        fill={C.white}
        stroke={C.ink50}
        strokeWidth={1.4}
      />
    );
  });

  const labels = pts.map((p, i) => (
    <text
      key={`label-${p.id}`}
      x={x(i)}
      y={labelBottom}
      textAnchor="middle"
      style={{ ...MONO, fontSize: 10, fill: C.ink50 }}
    >
      R{rounds[i].sequence}
    </text>
  ));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      style={{ flex: "0 0 auto", overflow: "visible" }}
      role="img"
      aria-label="Evolução do nível por round"
    >
      <text
        x={0}
        y={titleTop}
        style={{
          ...UI,
          fontSize: 10,
          fontWeight: 600,
          fill: C.ink50,
          textTransform: "uppercase",
        }}
      >
        Evolução
      </text>
      {segments}
      {dots}
      {labels}
    </svg>
  );
}
